import React, { useState, useEffect, useRef } from "react";
import { BiChevronDown, BiSearch } from 'react-icons/bi'
import SelectEnterpriseStyle from '../../../../assets/css/components/admin/menus/components/SelectEnterpriseStyle'
import { keyUserId, keyAccessToken, keyUserActiveEnterpriseId, parentEntId } from '../../../../assets/js/SessionStorageNames';
import { useProviders } from '../../../../hooks/api/clientsProvides';
import { Spinner } from 'react-bootstrap';

export default function SelectClientProvider() {
    const { 
        providersData, 
        selectedProviderId, 
        loading, 
        error, 
        isSearching,
        fetchProviders, 
        selectProvider,
        searchProviders
    } = useProviders();
    
    const [idOption, setIdOption] = useState("");
    const [openList, setOpenList] = useState(false);
    const [enterPriseText, setEnterprisetext] = useState('Debe seleccionar un proveedor');
    const [searchTerm, setSearchTerm] = useState('');
    const [initialLoading, setInitialLoading] = useState(true);
    const wrapperRef = useRef(null);
    
    // Mostrar proveedores actuales (ya filtrados por el backend si hay búsqueda)
    const filteredProviders = providersData;
    
    // Función helper para guardar proveedor en localStorage (elimina duplicación)
    const saveProviderToLocalStorage = (provider) => {
        if (!provider) return;
        
        // Crear un nuevo array con solo el proveedor seleccionado
        const updatedProviders = [{
            _id: provider._id,
            ownerId: provider.ownerId || provider.odooId,
            ownerName: provider.name || provider.ownerName,
            parentId: provider.parentId?._id,
            selectedAt: new Date().toISOString()
        }];
        
        // Guardar el array actualizado en localStorage (reemplaza completamente)
        localStorage.setItem('selectedProviders', JSON.stringify(updatedProviders));
        console.log('Proveedor guardado en localStorage:', provider.name || provider.ownerName);
    };
    
    // Función helper para recuperar proveedor del localStorage (elimina duplicación)
    const getSavedProviderFromLocalStorage = () => {
        try {
            const savedProviders = JSON.parse(localStorage.getItem('selectedProviders') || '[]');
            return savedProviders.length > 0 ? savedProviders[0] : null;
        } catch (error) {
            console.error('Error al leer proveedor guardado del localStorage:', error);
            return null;
        }
    };
    
    // Función helper para restaurar proveedor guardado (elimina duplicación)
    const restoreSavedProvider = (data) => {
        if (!data || data.length === 0) return;
        
        const lastSelectedProvider = getSavedProviderFromLocalStorage();
        if (!lastSelectedProvider) return;
        
        const providerExists = data.find(provider => provider._id === lastSelectedProvider._id);
        if (providerExists) {
            console.log('Recuperando provider guardado:', lastSelectedProvider.ownerName);
            setIdOption(lastSelectedProvider._id);
            selectProvider(lastSelectedProvider._id);
            setEnterprisetext(lastSelectedProvider.ownerName);
        }
    };
    
    const handleSelectProvider = async (providerId) => {
        try {
            setIdOption(providerId);
            await selectProvider(providerId);
            
            // Guardar el proveedor seleccionado en el localStorage (reemplazar completamente)
            const selectedProvider = providersData.find(provider => provider._id === providerId);
            if (selectedProvider) {
                saveProviderToLocalStorage(selectedProvider);
            }
        } catch (error) {
            console.error('Error al seleccionar proveedor:', error);
            // Mostrar mensaje de error al usuario
            const errorMessage = error?.response?.data?.message || error?.message || 'Error al seleccionar el proveedor. Por favor, intente nuevamente.';
            alert(`Error: ${errorMessage}`);
            // Revertir el cambio de idOption si falla
            const savedProvider = getSavedProviderFromLocalStorage();
            if (savedProvider) {
                setIdOption(savedProvider._id);
            } else {
                setIdOption('');
            }
        }
    };

  /**
   * Get the dropdown options
   */
   useEffect(() => { 
    const currentEntId = "5f1378dcef2f752aeda16673";
    console.log('Cargando proveedores para enterprise:', currentEntId);
    
    fetchProviders(currentEntId)
      .then((data) => {
        // Si data es null, el request fue cancelado por ser obsoleto, ignorar
        if (data === null) {
          console.log('Request cancelado (obsoleto), ignorando resultado');
          // Verificar si ya hay datos en el contexto del request más reciente
          // Si los hay, actualizar initialLoading para evitar quedarse atascado
          if (providersData && providersData.length > 0) {
            console.log('Datos ya disponibles en contexto, actualizando initialLoading');
            setInitialLoading(false);
          }
          return;
        }
        
        console.log('Providers data cargada exitosamente:', data);
        setInitialLoading(false);
        
        // Después de cargar los datos, intentar recuperar el proveedor guardado
        restoreSavedProvider(data);
      })
      .catch((error) => {
        console.error('Error al cargar proveedores:', error);
        setEnterprisetext('Error al cargar los proveedores');
        setInitialLoading(false);
      });
    //eslint-disable-next-line
  }, []);

  // Efecto adicional para asegurar que initialLoading se actualice cuando providersData esté disponible
  // Esto maneja el caso donde el request inicial fue obsoleto pero un request más reciente ya completó
  useEffect(() => {
    if (initialLoading && providersData && providersData.length > 0) {
      console.log('Providers data disponible, actualizando initialLoading');
      setInitialLoading(false);
      
      // Intentar recuperar el proveedor guardado si aún no se ha seleccionado uno
      if (!selectedProviderId) {
        restoreSavedProvider(providersData);
      }
    }
    //eslint-disable-next-line
  }, [providersData, initialLoading, selectedProviderId]);



  useEffect(() => {
    const parent = providersData.find(v => v._id.toString()===idOption.toString())?.parentId?._id;
    if (parent) {
      localStorage.setItem(parentEntId, parent);
    } else {
      localStorage.removeItem(parentEntId);
    }
  },[idOption, providersData])
  
  useEffect(() => {
    if(providersData.length === 0) return;
    if(idOption.length === 0) return;
    const valueText = providersData.find(v => v._id === idOption);
    if(valueText !== undefined) {
      setEnterprisetext(valueText.name || valueText.ownerName);
    }else {
      setEnterprisetext('Debe seleccionar un proveedor');
    }
  }, [idOption, providersData]);

  // Efecto adicional para sincronizar cuando cambie el selectedProviderId del contexto global
  useEffect(() => {
    if (selectedProviderId && providersData.length > 0 && selectedProviderId !== idOption) {
      const provider = providersData.find(v => v._id === selectedProviderId);
      if (provider) {
        setIdOption(selectedProviderId);
        setEnterprisetext(provider.name || provider.ownerName);
        selectProvider(selectedProviderId);
      }
    }
  }, [selectedProviderId, providersData?.length]); // Solo cuando cambie la cantidad de providers

  function useOutsideAlerter(ref) {
    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
            setOpenList(false);
        }
      }
      // Bind the event listener
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }
  useOutsideAlerter(wrapperRef);

  if (initialLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center p-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Cargando proveedores...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <SelectEnterpriseStyle.ContSelect ref={wrapperRef}>
        <SelectEnterpriseStyle.SelectCustom>
          <SelectEnterpriseStyle.Text size="12pt" fw="500" cl="#ea5455">
            Error al cargar proveedores
          </SelectEnterpriseStyle.Text>
        </SelectEnterpriseStyle.SelectCustom>
      </SelectEnterpriseStyle.ContSelect>
    );
  }

  return (
    <SelectEnterpriseStyle.ContSelect ref={wrapperRef}>
      <SelectEnterpriseStyle.SelectCustom onClick={() => setOpenList(!openList)}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ flex: 1 }}>
            {loading ? (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                <SelectEnterpriseStyle.Text size="12pt" fw="500" cl="#707070">
                  Cargando datos...
                </SelectEnterpriseStyle.Text>
              </div>
            ) : (
              <SelectEnterpriseStyle.Text size="12pt" fw="500" cl={idOption.length === 0 ? '#ea5455' : '#707070'}>
                {enterPriseText}
              </SelectEnterpriseStyle.Text>
            )}
          </div>
          
          <SelectEnterpriseStyle.BtnShowOptions>
            <BiChevronDown />
          </SelectEnterpriseStyle.BtnShowOptions>
        </div>
      </SelectEnterpriseStyle.SelectCustom>
      
      <SelectEnterpriseStyle.ContListOptions active={openList}>
        <SelectEnterpriseStyle.SearchBox>
          <BiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar proveedor por nombre..."
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              // Llamar a la función de búsqueda con debounce
              searchProviders(value);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </SelectEnterpriseStyle.SearchBox>
        
        <SelectEnterpriseStyle.ListEnterprises>
        {isSearching ? (
          <div className="d-flex align-items-center justify-content-center p-3">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>Buscando proveedores...</span>
          </div>
        ) : filteredProviders.length === 0 ? (
          <div className="text-center p-3 text-muted">
            {searchTerm ? 'No se encontraron proveedores' : 'No hay proveedores disponibles'}
          </div>
        ) : (
          filteredProviders.map((values) => (
            <SelectEnterpriseStyle.ItemListEnterprise 
              key={values._id} 
              onClick={async () => {
                try {
                  setEnterprisetext((values.name || values.ownerName || '').toUpperCase())
                  setOpenList(false)
                  setSearchTerm('')
                  // Esperar a que handleSelectProvider complete
                  // selectProvider ya resetea la búsqueda internamente, no es necesario llamar a searchProviders
                  await handleSelectProvider(values._id)
                } catch (error) {
                  console.error('Error en onClick de selección de proveedor:', error);
                  // El error ya se maneja en handleSelectProvider, pero aquí podemos revertir el estado visual
                  setOpenList(true); // Mantener la lista abierta si hay error
                }
              }}
            >
              <SelectEnterpriseStyle.Text size="12pt">
                {(values.name || values.ownerName || '').toUpperCase()}
              </SelectEnterpriseStyle.Text>
            </SelectEnterpriseStyle.ItemListEnterprise>
          ))
        )}
        </SelectEnterpriseStyle.ListEnterprises>
      </SelectEnterpriseStyle.ContListOptions>
    </SelectEnterpriseStyle.ContSelect>
  )
}
